/**
 * useMarkupLayer — everything a photo viewer needs to show and edit the drawing on the photo on
 * screen (gallery F-6): the toolbar buttons (Draw, Hide/Show drawing), the layer for the viewer's
 * `overlay` slot, and the editor panel for its `editingPanel` slot. GalleryLightbox and
 * LibraryLightbox both use it, so there is one drawing implementation.
 *
 * `canDraw` is the caller's statement that the FULL-SIZE copy has LOADED on screen (the viewer
 * reports it through onImageLoad): drawing on a thumbnail that is about to be swapped for the sharp
 * copy would shift under the pen.
 *
 * `photo` undefined means "no drawing here": no read, no buttons, no layer. The shared viewers pass
 * undefined unless their host opted in with `showMarkup` (customer isolation is opt-in).
 *
 * SAVING. The editor remembers the revision it opened on (state.baseRevision) and every write names
 * that one, never the live query's: a background refetch mid-edit must not let this save erase marks
 * it never saw. On a conflict the newer drawing is fetched and the editor's own changes are laid over
 * it by shape id (rebase); the editor stays open for the user to check and save.
 *
 * NEVER CUSTOMER-FACING (allow-listed in scripts/guards/check-markup-isolation.mjs).
 */
import { useCallback, useEffect, useMemo, useReducer, useState, type ReactNode } from 'react';
import { Eye, EyeOff, Pencil, RefreshCw } from 'lucide-react';
import {
  useClearGalleryMarkup,
  useGalleryMarkup,
  useMarkupRevisions,
  useSaveGalleryMarkup,
} from '../../../../hooks/fieldService/useGalleryMarkup';
import type { GeneratorPhoto } from '../../../../types/fieldService/generatorPhotos';
import { initialEditorState, markupEditorReducer, restoredFromOf } from './markupEditorState';
import { MarkupCanvas } from './MarkupCanvas';
import { MarkupOverlay } from './MarkupOverlay';
import { MarkupPanel, type MarkupHistory } from './MarkupPanel';

export interface MarkupLayer {
  editing: boolean;
  /** Buttons for the viewer toolbar (via `toolbarExtra`). */
  toolbarButtons: ReactNode;
  /** For the viewer's `overlay` slot, for the photo on screen. */
  overlay: ReactNode;
  /** For the viewer's `editingPanel` slot. */
  panel: ReactNode;
}

// 44px touch targets, like the editor's own buttons (tech phones).
const TOOLBAR_BTN =
  'min-h-[44px] min-w-[44px] p-2 rounded-full transition-colors flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed';

function isConflict(err: unknown): boolean {
  return (err as { code?: unknown } | null)?.code === 'functions/aborted';
}

function revisionLabel(name: string | undefined, when: Date | undefined): string {
  const date = when ? when.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null;
  return [name, date].filter(Boolean).join(', ') || 'earlier version';
}

export function useMarkupLayer(
  photo: GeneratorPhoto | undefined,
  canDraw: boolean,
  /** Why Draw is off when `canDraw` is false; defaults to "still loading". */
  cannotDrawReason = 'Loading the full-size photo...'
): MarkupLayer {
  const photoId = photo?.id ?? null;
  const markup = useGalleryMarkup(photoId);
  const save = useSaveGalleryMarkup(photoId ?? '');
  const clear = useClearGalleryMarkup(photoId ?? '');
  const [editing, setEditing] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [state, dispatch] = useReducer(markupEditorReducer, [], initialEditorState);
  const revisions = useMarkupRevisions(photoId, state.baseRevision, editing && historyOpen);

  // No photo handed over (the host did not opt in) means no drawing, whatever a cache holds.
  const saved = photo ? (markup.data ?? null) : null;
  const shapes = useMemo(() => saved?.shapes ?? [], [saved]);
  const photoChanged = Boolean(saved?.contentSha256 && photo?.contentSha256 && saved.contentSha256 !== photo.contentSha256);
  const loadFailed = Boolean(markup.isError);

  // A different photo on screen (the viewer blocks this while editing) starts clean.
  useEffect(() => {
    setEditing(false);
    setHistoryOpen(false);
  }, [photoId]);

  const start = useCallback(() => {
    dispatch({ type: 'reset', shapes, revision: saved?.revision ?? 0 });
    setHidden(false);
    setHistoryOpen(false);
    setEditing(true);
  }, [saved?.revision, shapes]);

  const stop = useCallback(() => setEditing(false), []);

  const onSave = useCallback(async () => {
    const expectedRevision = state.baseRevision;
    const restoredFrom = restoredFromOf(state);
    try {
      if (state.shapes.length === 0 && expectedRevision > 0 && restoredFrom === null) {
        await clear.mutateAsync({ expectedRevision });
      } else {
        await save.mutateAsync({ shapes: state.shapes, expectedRevision, restoredFrom });
      }
      setEditing(false);
    } catch (err) {
      // The hook already told the user why. On a conflict, fetch the newer drawing and lay this
      // editor's changes over it; the editor stays open so they can check it and save again.
      if (!isConflict(err)) return;
      const fresh = await markup.refetch();
      const latest = fresh.data ?? null;
      dispatch({ type: 'rebase', shapes: latest?.shapes ?? [], revision: latest?.revision ?? 0 });
    }
  }, [clear, markup, save, state]);

  const history: MarkupHistory = {
    open: historyOpen,
    available: state.baseRevision > 1,
    onToggle: () => setHistoryOpen((o) => !o),
    loading: revisions.isLoading,
    error: revisions.isError,
    onRetry: () => void revisions.refetch(),
    items: (revisions.data?.pages ?? []).flat().map((r) => ({
      revision: r.revision,
      label: revisionLabel(r.updatedBy?.name, r.updatedAt?.toDate?.()),
      markCount: r.shapes.length,
      onRestore: () => dispatch({ type: 'restore', shapes: r.shapes, revision: r.revision }),
    })),
    hasMore: Boolean(revisions.hasNextPage),
    loadingMore: revisions.isFetchingNextPage,
    onMore: () => void revisions.fetchNextPage(),
  };

  const drawTitle = loadFailed
    ? "The drawing couldn't be loaded, so it can't be edited. Retry first."
    : canDraw
      ? 'Draw on photo'
      : cannotDrawReason;

  const toolbarButtons = photo ? (
    <>
      {shapes.length > 0 && (
        <button
          type="button"
          onClick={() => setHidden((h) => !h)}
          aria-pressed={!hidden}
          aria-label={hidden ? 'Show drawing' : 'Hide drawing'}
          title={hidden ? 'Show drawing' : 'Hide drawing'}
          className={`${TOOLBAR_BTN} bg-white/10 text-white hover:bg-white/20`}
        >
          {hidden ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
        </button>
      )}
      {loadFailed && (
        <button
          type="button"
          onClick={() => void markup.refetch()}
          aria-label="Retry loading the drawing"
          title="Retry loading the drawing"
          className={`${TOOLBAR_BTN} bg-amber-500/20 text-amber-100 hover:bg-amber-500/30`}
        >
          <RefreshCw className="h-5 w-5" />
        </button>
      )}
      <button
        type="button"
        onClick={start}
        disabled={!canDraw || markup.isLoading || loadFailed}
        aria-label="Draw on photo"
        title={drawTitle}
        className={`${TOOLBAR_BTN} bg-white/10 text-white hover:bg-white/20`}
      >
        <Pencil className="h-5 w-5" />
      </button>
    </>
  ) : null;

  const overlay = editing ? (
    <MarkupCanvas state={state} dispatch={dispatch} />
  ) : !hidden && shapes.length > 0 ? (
    <MarkupOverlay shapes={shapes} photoChanged={photoChanged} />
  ) : null;

  const panel = editing ? (
    <MarkupPanel
      state={state}
      dispatch={dispatch}
      onSave={onSave}
      onCancel={stop}
      saving={save.isPending || clear.isPending}
      history={history}
    />
  ) : null;

  return { editing, toolbarButtons, overlay, panel };
}
